import { MetadataQrPrefill } from "components/MetadataQrPrefill";
import { metadataFields } from "lib/metadata-qr";
import { Storage, StorageKey } from "lib/storage";
import { decodeJwt } from "jose";
import { useCallback, useEffect, useRef, useState } from "react";
import { Field } from "components/Form/Field";
import { Input } from "components/Form/Input";
import { Select } from "components/Form/Select";
import clsx from "clsx";
import { captureDB } from "lib/indexed-db";
import { parseKeyValueLines } from "lib/key-value-lines";
import {
  getDropdownOptions,
  getLabelProposals,
  getStepDropdownValues,
} from "lib/label-relations";
import { LabelRelation, PrismicCollectionDetailField } from "types";
import { TaskStepLabel } from "lib/task-steps";

const emptyUserProfile = async (): Promise<Record<string, unknown>> => ({});

interface StepDetailsProps {
  stepDetailsFields: any[];
  stepNumber: number;
  onValidationRegister?: (
    validateFn: () => Array<{ id: string; title: string }>
  ) => void;
  onStepDetailsChange: (
    stepNumber: number,
    stepDetails: string[],
    stepDetailsFields?: Array<{
      label_id: string;
      label_title: string;
      label_type: string;
      value: string;
    }>
  ) => void;
  getStepDetails: (stepNumber: number) => string[] | undefined;
  taskId?: string;
  knownTaskValues?: Record<string, string>;
  loadUserProfile?: () => Promise<Record<string, unknown>>;
  labelRelations?: LabelRelation[];
  disabled?: boolean;
  dropdownValueSide?: "key" | "value";
  inlineQrPrefill?: boolean;
  heading?: string;
  description?: string;
  stepLabels?: TaskStepLabel[];
}

export function StepDetails({
  stepDetailsFields,
  stepNumber,
  onValidationRegister,
  onStepDetailsChange,
  getStepDetails,
  taskId,
  knownTaskValues,
  loadUserProfile = emptyUserProfile,
  labelRelations = [],
  disabled = false,
  dropdownValueSide = "key",
  inlineQrPrefill = false,
  heading,
  description,
  stepLabels,
}: StepDetailsProps) {
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [proposalError, setProposalError] = useState("");
  const [qrValues, setQrValues] = useState<Record<string, string>>({});
  const [knownLabelValues, setKnownLabelValues] = useState<
    Record<string, string>
  >({});
  const hydratedStepRef = useRef<number | null>(null);
  const hasUserEditedRef = useRef(false);
  const formDataRef = useRef<Record<string, string>>({});
  const touchedFieldIdsRef = useRef(new Set<string>());

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // Expose validation function through callback
  const validateAndGetMissingFields = useCallback(() => {
    const missingFields: Array<{ id: string; title: string }> = [];

    stepDetailsFields.forEach((field: any) => {
      if (
        field.is_required &&
        (!formData[field.label_id] || formData[field.label_id].trim() === "")
      ) {
        missingFields.push({
          id: field.label_id,
          title: field.label_title || "Field",
        });
        setErrors((prev) => ({
          ...prev,
          [field.label_id]: `This field is required`,
        }));
      }
    });

    return missingFields;
  }, [formData, stepDetailsFields]);

  // Register validation function with parent
  useEffect(() => {
    if (onValidationRegister) {
      onValidationRegister(validateAndGetMissingFields);
    }
  }, [onValidationRegister, validateAndGetMissingFields]);

  // Restore saved values when resuming or editing a completed step.
  useEffect(() => {
    const isNewStep = hydratedStepRef.current !== stepNumber;
    const currentStepDetails = getStepDetails(stepNumber);

    // A restore may finish after this component's first render. Hydrate that
    // late result unless the user has already started editing this step.
    if (!isNewStep && (hasUserEditedRef.current || !currentStepDetails)) return;

    hydratedStepRef.current = stepNumber;
    hasUserEditedRef.current = false;
    if (isNewStep) {
      touchedFieldIdsRef.current.clear();
      setQrValues({});
    }
    if (!currentStepDetails || currentStepDetails.length === 0) {
      formDataRef.current = {};
      setFormData({});
      setErrors({});
      return;
    }

    const restoredValues = Object.fromEntries(
      currentStepDetails.map((detail) => {
        const separatorIndex = detail.indexOf(":");
        return separatorIndex === -1
          ? [detail, ""]
          : [
              detail.slice(0, separatorIndex).trim(),
              detail.slice(separatorIndex + 1).trim(),
            ];
      })
    );
    formDataRef.current = restoredValues;
    setFormData(restoredValues);
    setErrors({});
  }, [stepNumber, getStepDetails]);

  useEffect(() => {
    if (hydratedStepRef.current !== stepNumber) {
      return;
    }
    let cancelled = false;

    const loadKnownValues = async () => {
      let userValues: Record<string, string> = {};
      let taskValues: Record<string, string> = {};
      try {
        const profile = await loadUserProfile();
        userValues = Object.fromEntries(
          Object.entries(profile)
            .filter(
              ([, value]) =>
                typeof value === "string" || typeof value === "number"
            )
            .map(([id, value]) => [id, String(value)])
        );
      } catch (error) {
        console.error("Could not load user label proposals:", error);
      }
      if (knownTaskValues !== undefined) {
        taskValues = knownTaskValues;
      } else if (taskId) {
        try {
          const [captures, details] = await Promise.all([
            captureDB.getStepCaptures(taskId),
            captureDB.getStepDetails(taskId),
          ]);
          const stepZeroValues =
            captures.find((capture) => capture.stepNumber === 0)
              ?.collectionDetails?.labels || {};
          const stepValues = Object.fromEntries(
            details.flatMap((detail) =>
              (detail.stepDetailsFields || []).flatMap((field) =>
                field.label_id && field.value
                  ? [[String(field.label_id), String(field.value)]]
                  : []
              )
            )
          );
          taskValues = { ...stepZeroValues, ...stepValues };
        } catch (error) {
          console.error("Could not load task label proposals:", error);
        }
      }
      if (cancelled) return;
      // Capture-step fields are intrinsic to the current capture. Session
      // values may hydrate task-wide fields, but must not silently choose the
      // next capture's settings. User values and values from this work task
      // remain available as explicit relationship sources.
      setKnownLabelValues({ ...userValues, ...taskValues });
    };

    loadKnownValues();
    return () => {
      cancelled = true;
    };
  }, [loadUserProfile, stepNumber, taskId, knownTaskValues]);

  useEffect(() => {
    if (hydratedStepRef.current !== stepNumber) return;
    if (disabled) {
      setProposalError("");
      return;
    }

    setProposalError("");
    try {
      const targets = stepDetailsFields.flatMap((field: any) => {
        if (field.label_type !== "dropdown") return [];
        return [
          {
            id: String(field.label_id),
            allowedValues:
              dropdownValueSide === "value"
                ? parseKeyValueLines(field.dropdown_options).map(
                    ({ value }) => value
                  )
                : getStepDropdownValues(field as PrismicCollectionDetailField),
            options: getDropdownOptions(field.dropdown_options).map(
              ({ baseValue, proposedValue }) => ({
                key: baseValue,
                value: proposedValue,
              })
            ),
            relatedLabelId: field._related_label_id,
          },
        ];
      });
      const proposals = getLabelProposals({
        relations: labelRelations,
        knownValues: {
          ...knownLabelValues,
          ...formData,
          ...Object.fromEntries(
            (stepLabels || []).map(({ field, value }) => [
              field.label_id?.toString().trim(),
              value,
            ])
          ),
        },
        targets,
      });
      const applicable = Object.fromEntries(
        Object.entries(proposals).filter(
          ([id]) =>
            !touchedFieldIdsRef.current.has(id) &&
            !formDataRef.current[id]?.trim()
        )
      );
      if (Object.keys(applicable).length > 0) {
        hasUserEditedRef.current = true;
        setFormData((current) => {
          const next = { ...current };
          Object.entries(applicable).forEach(([id, value]) => {
            if (!next[id]?.trim()) next[id] = value;
          });
          formDataRef.current = next;
          return next;
        });
      }
    } catch (error) {
      console.error("Invalid Prismic label proposal configuration:", error);
      setProposalError(
        "This task has an invalid label proposal configuration."
      );
    }
  }, [
    formData,
    disabled,
    dropdownValueSide,
    knownLabelValues,
    labelRelations,
    stepDetailsFields,
    stepNumber,
    stepLabels,
  ]);

  useEffect(() => {
    if (
      disabled ||
      getStepDetails(stepNumber) !== undefined ||
      !taskId ||
      !stepDetailsFields.some((field) => field.reuse_previous_value === true)
    )
      return;
    let cancelled = false;
    captureDB
      .getStepDetails(taskId)
      .then((details) => {
        if (cancelled) return;
        const previous = Object.fromEntries(
          details
            .filter(
              (item) => item.stepNumber > 0 && item.stepNumber < stepNumber
            )
            .sort((a, b) => a.stepNumber - b.stepNumber)
            .flatMap((item) =>
              (item.stepDetailsFields || []).map((field) => [
                field.label_id,
                field.value,
              ])
            )
        );
        const additions = Object.fromEntries(
          stepDetailsFields
            .filter(
              (field) =>
                field.reuse_previous_value === true &&
                !touchedFieldIdsRef.current.has(field.label_id) &&
                !formDataRef.current[field.label_id]?.trim() &&
                previous[field.label_id]
            )
            .flatMap((field) => {
              const value = previous[field.label_id];
              const options = parseKeyValueLines(field.dropdown_options);
              if (
                field.label_type === "dropdown" &&
                !options.some((option) => option[dropdownValueSide] === value)
              )
                return [];
              return [[field.label_id, value]];
            })
        );
        if (Object.keys(additions).length) {
          hasUserEditedRef.current = true;
          setFormData((current) => {
            const next = { ...additions, ...current };
            formDataRef.current = next;
            return next;
          });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [
    taskId,
    stepNumber,
    stepDetailsFields,
    dropdownValueSide,
    getStepDetails,
    disabled,
  ]);

  const handleFieldChange = useCallback(
    (fieldId: string, value: string) => {
      hasUserEditedRef.current = true;
      touchedFieldIdsRef.current.add(fieldId);
      setQrValues((current) => {
        const next = { ...current };
        delete next[fieldId];
        return next;
      });
      formDataRef.current = { ...formDataRef.current, [fieldId]: value };
      setFormData((prev) => ({
        ...prev,
        [fieldId]: value,
      }));

      // Clear error when user starts typing
      if (errors[fieldId]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[fieldId];
          return newErrors;
        });
      }
    },
    [errors]
  );

  // Auto-save step details when form data changes
  useEffect(() => {
    if (hydratedStepRef.current !== stepNumber) return;
    if (!hasUserEditedRef.current) return;

    const stepDetails = stepDetailsFields
      .map((field: any) => {
        const value = formData[field.label_id];
        return value && value.trim() !== ""
          ? `${field.label_id}: ${value}`
          : "";
      })
      .filter((value: string) => value && value.trim() !== "");

    // Save empty arrays too, otherwise clearing the final value restores stale
    // metadata the next time the user opens the step.
    const fieldsWithValues = stepDetailsFields
      .filter(
        (field: any) =>
          formData[field.label_id] && formData[field.label_id].trim() !== ""
      )
      .map((field: any) => ({
        label_id: field.label_id,
        label_title: field.label_title,
        label_type: field.label_type || "text",
        value: formData[field.label_id],
      }));

    onStepDetailsChange(stepNumber, stepDetails, fieldsWithValues);
  }, [formData, onStepDetailsChange, stepDetailsFields, stepNumber]);

  const renderField = useCallback(
    (field: any) => {
      const fieldId = field.label_id;
      const fieldType = field.label_type;
      const fieldTitle = field.label_title;
      const dropdownOptions = field.dropdown_options;

      switch (fieldType) {
        case "text":
          return (
            <Input
              key={fieldId}
              aria-label={fieldTitle}
              placeholder={fieldTitle}
              value={formData[fieldId] || ""}
              disabled={disabled}
              onChange={(e) => handleFieldChange(fieldId, e.target.value)}
            />
          );

        case "numeric":
          return (
            <Input
              key={fieldId}
              type="number"
              aria-label={fieldTitle}
              placeholder={fieldTitle}
              value={formData[fieldId] || ""}
              disabled={disabled}
              onChange={(e) => handleFieldChange(fieldId, e.target.value)}
            />
          );

        case "date":
          return (
            <Input
              key={fieldId}
              aria-label={fieldTitle}
              type="date"
              value={formData[fieldId] || ""}
              disabled={disabled}
              onChange={(e) => handleFieldChange(fieldId, e.target.value)}
            />
          );

        case "dropdown":
          if (!dropdownOptions) return null;

          const options = parseKeyValueLines(dropdownOptions);

          // Orb uses the same stored option values as collection details;
          // phone steps retain their existing key-based metadata.
          const optionsObject = options.reduce(
            (
              acc: Record<string, string>,
              opt: { key: string; value: string }
            ) => {
              if (dropdownValueSide === "value") acc[opt.key] = opt.value;
              else acc[opt.value] = opt.key;
              return acc;
            },
            {}
          );

          return (
            <Select
              key={fieldId}
              value={formData[fieldId] || ""}
              onChange={(value) => handleFieldChange(fieldId, value as string)}
              options={optionsObject}
              placeholder="Select label"
              renderValue={(value) =>
                options.find((option) => option[dropdownValueSide] === value)
                  ?.key ?? value
              }
              renderListItem={(item) =>
                dropdownValueSide === "value"
                  ? `${item.label}:${item.value}`
                  : `${item.value}:${item.label}`
              }
              disabled={disabled}
            />
          );

        default:
          return null;
      }
    },
    [disabled, dropdownValueSide, formData, handleFieldChange]
  );

  const qrFields = metadataFields(
    stepDetailsFields.filter((field) => field.qr_prefill === true)
  ).map((field) => ({ ...field, dropdownValueSide }));
  // A single session field gets a scan action beside its manual input.
  // Other forms use one scan action in their collection-details header.
  const inlineQrField =
    inlineQrPrefill && qrFields.length === 1 ? qrFields[0].id : undefined;
  const scanner = (
    <MetadataQrPrefill
      key={`${taskId || ""}:${stepNumber}`}
      fields={qrFields}
      iconOnly
      triggerLabel={
        inlineQrField ? `Scan ${qrFields[0].title}` : "Scan collection details"
      }
      values={formData}
      disabled={disabled}
      collectionId={(() => {
        try {
          const token = Storage.get(StorageKey.TOKEN);
          return token ? String(decodeJwt(token).contributor || "") : undefined;
        } catch {
          return undefined;
        }
      })()}
      onApply={(values) => {
        hasUserEditedRef.current = true;
        Object.keys(values).forEach((id) => touchedFieldIdsRef.current.add(id));
        setQrValues((current) => ({ ...current, ...values }));
        const next = { ...formDataRef.current, ...values };
        formDataRef.current = next;
        setFormData(next);
        setErrors((current) =>
          Object.fromEntries(
            Object.entries(current).filter(([id]) => !(id in values))
          )
        );
      }}
    />
  );

  return (
    <div
      className={clsx("space-y-4", {
        "rounded-xl border border-f4f7ff bg-white p-4":
          !inlineQrField && qrFields.length > 0,
      })}
    >
      {!inlineQrField && (heading || qrFields.length > 0) && (
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold">
              {heading || "Collection details"}
            </h2>
            {description && (
              <p className="mt-1 text-sm text-858494">{description}</p>
            )}
          </div>
          {scanner}
        </div>
      )}
      {proposalError && (
        <p
          className="rounded-xl bg-red-50 p-3 text-sm text-fd380f"
          role="alert"
        >
          {proposalError}
        </p>
      )}
      {stepDetailsFields.map((field: any) => (
        <Field
          key={field.label_id}
          label={
            <span className="flex items-center justify-between gap-3">
              <span>{field.label_title}</span>
              {qrValues[field.label_id] &&
                qrValues[field.label_id] === formData[field.label_id] && (
                  <span className="shrink-0 text-sm font-normal text-3461fd">
                    From QR
                  </span>
                )}
            </span>
          }
          description={errors[field.label_id]}
          descriptionClassName={clsx({
            "text-fd380f": errors[field.label_id],
          })}
        >
          <div id={`step-detail-${field.label_id}`}>
            {field.label_id === inlineQrField ? (
              <div
                role="group"
                aria-label={`${
                  field.label_title || field.label_id
                } input methods`}
                className="flex items-center gap-2.5"
              >
                <div className="min-w-0 flex-1">{renderField(field)}</div>
                {scanner}
              </div>
            ) : (
              renderField(field)
            )}
          </div>
        </Field>
      ))}
      {stepLabels?.map(({ field, value }) => (
        <Field
          key={field.label_id?.toString()}
          label={field.label_title?.toString()}
        >
          <Select
            value={value}
            options={Object.fromEntries(
              parseKeyValueLines(field.dropdown_options).map(
                ({ key, value }) => [key, value]
              )
            )}
            renderValue={(value) =>
              parseKeyValueLines(field.dropdown_options).find(
                (option) => option.value === value
              )?.key || value
            }
            onChange={() => {}}
            disabled
          />
        </Field>
      ))}
    </div>
  );
}
